import React, { useCallback, useEffect, useRef, useState } from "react";
import { css, Theme, useTheme } from "@emotion/react";
import OutsideClickHandler from "react-outside-click-handler";

import Loading from "core/Components/Utils/Loading";

type Props = {
    isEmptyItems: boolean,
    onChange: (discountCode: string) => Promise<void>,
    appliedDiscountCode: string,
};

const ItemDiscountCodeInputWithCode: React.FC<Props> = (
    {
        isEmptyItems,
        onChange,
        appliedDiscountCode = "",
    }
) => {
    const theme: Theme = useTheme();
    const [discountCode, setDiscountCode] = useState("");
    const discountCodeHolder = useRef("");
    const [isLoading, setIsLoading] = useState(false);

    // If promo code is already set on the cart, then set discount to it.
    useEffect(() => {
        if (appliedDiscountCode) {
            discountCodeHolder.current = appliedDiscountCode;
            setDiscountCode(appliedDiscountCode);
        }
    }, [appliedDiscountCode]);

    const isDeletingCode = !isEmptyItems && !!appliedDiscountCode && appliedDiscountCode === discountCode;

    const onChangeEvent = (e: React.ChangeEvent<HTMLInputElement>) => {
        const promoCode = e.target.value.toUpperCase();
        discountCodeHolder.current = promoCode;
        setDiscountCode(promoCode);
    };

    const buttonClickHandler = () => {
        setIsLoading(true);
        if (isDeletingCode) {
            setDiscountCode("");
            discountCodeHolder.current = "";
        }

        return onChange(isDeletingCode ? "" : discountCode).then(() => {
            setIsLoading(false);
        });
    };

    const onResetValue = useCallback(() => {
        if (discountCode !== appliedDiscountCode || (!discountCode && appliedDiscountCode)) {
            setDiscountCode(appliedDiscountCode)
            discountCodeHolder.current = appliedDiscountCode;
        }
    }, [appliedDiscountCode, discountCode]);

    const discountContainerStyle = css`
        > input {
            border: 2px solid ${theme.colours.malibu4};
        }

        > div {
            border: 2px solid ${theme.colours.malibu4};
            background-color: ${theme.colours.malibu4};
        }
    `;

    const style = css`
        position: relative;
        display: flex;

        > input:focus {
            outline: none;
        }

        ${!!discountCode && css`
            ${discountContainerStyle};
        `}
    `;

    const inputStyle = css`
        width: 136px;
        height: 33px;
        text-align: center;
        border: 2px solid ${theme.colours.anakiwa2};
        border-radius: 5px 0 0 5px;

        ${theme.breakpoints.down('lg')} {
            width: 129px;
            height: 28px;
            font-size: 13px;
        }
    `;

    const buttonStyle = css`
        position: relative;
        display: flex;
        justify-content: center;
        align-items: center;
        width: 65px;
        color: white;
        border-radius: 0 5px 5px 0;
        background-color: ${theme.colours.anakiwa2};
        cursor: pointer;

        ${(!discountCode || isLoading) && css`
            cursor: default;
            pointer-events: none;
        `}
        ${theme.breakpoints.down('lg')} {
            font-size: 13px;
        }
    `;

    return (
        <OutsideClickHandler onOutsideClick={onResetValue}>
            <div css={style}>
                <input
                    type={'text'}
                    value={discountCodeHolder.current}
                    onChange={onChangeEvent}
                    css={inputStyle}
                    placeholder={'Enter promo code'}
                    readOnly={isEmptyItems}
                />
                <div css={buttonStyle} onClick={buttonClickHandler}>
                    {isLoading && !!discountCode && (
                        <Loading small ringIndicator onTop overlayRingIndicator />
                    )}
                    {isDeletingCode ? 'Delete' : 'Apply'}
                </div>
            </div>
        </OutsideClickHandler>
    );
};

export default ItemDiscountCodeInputWithCode;